import type {
  ClientOrganizerSettingsSchema,
  ClientPlan,
  CustomQuestionCategory,
  JSON,
  QuestionnaireTopicCategory,
  QuestionnaireTopicSection,
  SDKRequestFor
} from '@st/sdk'
import { deepEqual } from '@st/util/deep-equal'
import { jsonMapDiff } from '@st/util/json-diff'
import { Effect } from 'effect'
import {
  BrowserResources,
  FileStorage,
  IdGenerator,
  Routing,
  sdk,
  type BrowserResourceRef,
  type SnackbarVariant
} from '~/effects'
import { privateDocumentPath } from '~/features/file-storage/storage-paths'

export const RETURN_TYPES = ['1040', '1120', '1120s', '1065'] as const

export const CLIENT_ORGANIZER_MFA_ENABLED_KEY = 'client_organizer.mfa_enabled'
export const TAX_RETURN_DELIVERY_KBA_ENABLED_KEY = 'tax_return_delivery.kba_enabled'
export const CLIENT_ORGANIZER_DASHBOARD_NOTE_KEY = 'client_organizer.dashboard_note'
export const DEFAULT_CLIENT_ORGANIZER_DASHBOARD_NOTE = `Hi $clientname,

We set up this organizer to make it easier for you to provide your information for Tax Year $year. Once you complete the items below, please click "Submit" to let us know you're finished. If you have any questions, don't hesitate to reach out.

-$orgname`

/** An organization's settings for one return type. */
export type FolderSettings = {
  settings: Record<string, JSON>
  plan: ClientPlan
}

/** The part of the settings a person edits; the engagement letter is saved on its own. */
export type FolderSettingsDraft = {
  settings: Record<string, JSON>
  plan: Pick<ClientPlan, 'requireSpouseSignature'>
}

/** Which organization and return type a read or write is for. */
export type SettingsScope = {
  organizationId: string
  returnType: string
}

export type FolderSettingsRequest =
  | SDKRequestFor<'organizations/updateFolderSettings'>
  | SDKRequestFor<'organizations/updateClientPlan'>

export type SaveResult = { request: FolderSettingsRequest; ok: boolean }

// ---------- reading ----------

export function folderSettingsRequest(
  scope: SettingsScope
): SDKRequestFor<'organizations/getFolderSettings'> {
  return {
    type: 'organizations/getFolderSettings',
    organizationId: scope.organizationId,
    returnType: scope.returnType
  }
}

// ---------- saving a draft ----------

export function draftOf({ settings, plan }: FolderSettings): FolderSettingsDraft {
  return { settings, plan: { requireSpouseSignature: plan.requireSpouseSignature } }
}

/** The requests that would make the persisted settings match the draft; none means saved. */
export function saveRequests(
  scope: SettingsScope,
  persisted: FolderSettingsDraft,
  draft: FolderSettingsDraft
): FolderSettingsRequest[] {
  const { organizationId, returnType } = scope
  const requests: FolderSettingsRequest[] = []
  const changes = settingsChanges(persisted.settings, draft.settings)
  if (Object.keys(changes).length > 0) {
    requests.push({
      type: 'organizations/updateFolderSettings',
      organizationId,
      returnType,
      settings: changes
    })
  }
  if (persisted.plan.requireSpouseSignature != draft.plan.requireSpouseSignature) {
    requests.push({
      type: 'organizations/updateClientPlan',
      organizationId,
      returnType,
      requireSpouseSignature: draft.plan.requireSpouseSignature
    })
  }
  return requests
}

/**
 * Sends each request, then reads the settings back, so the outcome carries what
 * the server now has. A failed request is a result, never a failure; `persisted`
 * is undefined when the read-back fails.
 */
export function saveFolderSettings(scope: SettingsScope, requests: FolderSettingsRequest[]) {
  return Effect.gen(function* () {
    const results = yield* Effect.forEach(requests, (request) =>
      sdk.call(request, {
        onSuccess: (): SaveResult => ({ request, ok: true }),
        onFailure: (): SaveResult => ({ request, ok: false })
      })
    )
    const persisted = yield* sdk.call(folderSettingsRequest(scope), {
      onSuccess: ({ settings, plan }): FolderSettings | undefined => ({ settings, plan }),
      onFailure: () => undefined
    })
    return { results, persisted }
  })
}

export function savedNotice(
  returnType: string,
  results: SaveResult[]
): { text: string; variant?: SnackbarVariant } {
  const saved = results.filter((result) => result.ok).length
  if (saved == results.length) return { text: `Client organizer settings saved for ${returnType}` }
  if (saved == 0) return { text: 'Failed to save client organizer settings', variant: 'error' }
  return { text: 'Some client organizer settings could not be saved', variant: 'error' }
}

// ---------- the engagement letter ----------

export function uploadEngagementLetter(scope: SettingsScope, file: BrowserResourceRef<File>) {
  return Effect.gen(function* () {
    const pdf = yield* (yield* BrowserResources).take(file)
    const documentId = yield* (yield* IdGenerator).generate
    yield* (yield* FileStorage).upload(pdf, {
      destinationPath: privateDocumentPath(scope.organizationId, documentId),
      mimeType: 'application/pdf'
    })
    return yield* sdk.call({
      type: 'organizations/setClientContractTemplate',
      organizationId: scope.organizationId,
      returnType: scope.returnType,
      documentId,
      name: pdf.name
    })
  })
}

export function removeEngagementLetterRequest(
  scope: SettingsScope
): SDKRequestFor<'organizations/removeClientContractTemplate'> {
  return {
    type: 'organizations/removeClientContractTemplate',
    organizationId: scope.organizationId,
    returnType: scope.returnType
  }
}

/** Opens the stored letter in a new tab. */
export function openEngagementLetter(organizationId: string, documentId: string) {
  return Effect.gen(function* () {
    const url = yield* (yield* FileStorage).resolveURL(
      `storage://${privateDocumentPath(organizationId, documentId)}`
    )
    yield* (yield* Routing).openUrl(url)
    return null
  })
}

// ---------- reading the schema ----------

export function isCategoryVisible(
  category: QuestionnaireTopicCategory,
  settings: Record<string, JSON>
) {
  return !settings[category.hideKey ?? '']
}

export function topicCategoryCount(schema: ClientOrganizerSettingsSchema) {
  return schema.questionnaireTopicSettings.sections.reduce(
    (total, section) => total + sectionCategoryCount(section),
    0
  )
}

export function sectionCategoryCount(section: QuestionnaireTopicSection) {
  return section.groups.reduce((total, group) => total + group.categories.length, 0)
}

export function visibleSectionCategoryCount(
  section: QuestionnaireTopicSection,
  settings: Record<string, JSON>
) {
  return section.groups.reduce(
    (total, group) =>
      total + group.categories.filter((category) => isCategoryVisible(category, settings)).length,
    0
  )
}

export function includedQuestionCount(
  category: CustomQuestionCategory,
  settings: Record<string, JSON>
) {
  return category.questions.filter((question) => settings[question.key] == true).length
}

export function groupCustomQuestionCategories(categories: CustomQuestionCategory[]) {
  return {
    custom: categories.filter((category) => category.categoryType != 'state'),
    state: categories.filter((category) => category.categoryType == 'state')
  }
}

// ---------- internals ----------

function settingsChanges(
  persisted: Record<string, JSON>,
  draft: Record<string, JSON>
): Record<string, JSON> {
  return jsonMapDiff(persisted, draft, (a, b) => {
    if (a == null && b == null) return true
    return deepEqual(a, b)
  })
}
